import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import {
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
} from './ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './ui/tooltip';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { MessageSquare, ChevronRight, PanelRight, Lightbulb, Download } from 'lucide-react';
import { Comments } from './Comments';
import type { MentionableEntity } from './RichTextField';
import { SuggestionCard, type Suggestion } from './SuggestionCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '@/lib/utils';
import { TooltipProvider } from './ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Editor } from '@tiptap/core';
import { sortPendingSuggestionsByDocumentOrder } from '../utils/editor-utils';

const COMMENT_SIDEBAR_COOKIE_NAME = 'comment_sidebar_state';
const COMMENT_SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const COMMENT_SIDEBAR_WIDTH = '20rem';
const COMMENT_SIDEBAR_WIDTH_MOBILE = '18rem';
const COMMENT_SIDEBAR_WIDTH_ICON = '3rem';

type CommentSidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean | ((value: boolean) => boolean)) => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
};

const CommentSidebarShadcnContext = createContext<CommentSidebarContextProps | null>(null);

const useCommentSidebarShadcn = (): CommentSidebarContextProps => {
  const context = useContext(CommentSidebarShadcnContext);
  if (!context) {
    throw new Error('useCommentSidebarShadcn must be used within CommentSidebarProviderWrapper');
  }
  return context;
};

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 rounded-md px-4 py-5 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!text-center [&>span:last-child]:truncate [&>svg]:size-6 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const CommentSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      tooltip,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useCommentSidebarShadcn();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  }
);
CommentSidebarMenuButton.displayName = 'CommentSidebarMenuButton';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
};

type CommentSidebarContextType = {
  toggleSidebar: () => void;
  open: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
};

const CommentSidebarContext = createContext<CommentSidebarContextType | null>(null);

export const useCommentSidebar = (): CommentSidebarContextType => {
  const context = useContext(CommentSidebarContext);
  if (!context) {
    throw new Error('useCommentSidebar must be used within CommentSidebarProvider');
  }
  return context;
};

const CommentSidebarProviderWrapper: React.FC<{ children: React.ReactNode; defaultOpen?: boolean }> = ({ children, defaultOpen = true }) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof document === 'undefined') return defaultOpen;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => c.trim().startsWith(`${COMMENT_SIDEBAR_COOKIE_NAME}=`));
    if (cookie) {
      const value = cookie.split('=')[1];
      return value === 'true';
    }
    return defaultOpen;
  });

  const handleSetOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      setOpen(openState);
      if (typeof document !== 'undefined') {
        document.cookie = `${COMMENT_SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${COMMENT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    },
    [open]
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile
      ? setOpenMobile((prev) => !prev)
      : handleSetOpen((prev) => !prev);
  }, [isMobile, handleSetOpen]);

  const state: 'expanded' | 'collapsed' = open ? 'expanded' : 'collapsed';

  const contextValue: CommentSidebarContextProps = React.useMemo(
    () => ({
      state,
      open,
      setOpen: handleSetOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, handleSetOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <CommentSidebarShadcnContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={
            {
              '--sidebar-width': COMMENT_SIDEBAR_WIDTH,
              '--sidebar-width-icon': COMMENT_SIDEBAR_WIDTH_ICON,
            } as React.CSSProperties
          }
          className="group/sidebar-wrapper flex min-h-svh w-full"
        >
          {children}
        </div>
      </TooltipProvider>
    </CommentSidebarShadcnContext.Provider>
  );
};

const CommentSidebarContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sidebar = useCommentSidebarShadcn();

  const contextValue: CommentSidebarContextType = React.useMemo(
    () => ({
      toggleSidebar: sidebar.toggleSidebar,
      open: sidebar.open,
      openMobile: sidebar.openMobile,
      setOpenMobile: sidebar.setOpenMobile,
    }),
    [sidebar]
  );

  return (
    <CommentSidebarContext.Provider value={contextValue}>
      {children}
    </CommentSidebarContext.Provider>
  );
};

export const CommentSidebarProvider: React.FC<{ children: React.ReactNode; defaultOpen?: boolean }> = ({ children, defaultOpen = true }) => {
  return (
    <CommentSidebarProviderWrapper defaultOpen={defaultOpen}>
      <CommentSidebarContextProvider>
        {children}
      </CommentSidebarContextProvider>
    </CommentSidebarProviderWrapper>
  );
};

type CommentSidebarView = 'comments' | 'suggestions';

type CommentSidebarProps = {
  editor?: Editor | null;
  documentId?: string | null;
  isReadOnly?: boolean;
  comments?: Comment[];
  onLoadComments?: () => Promise<void>;
  deletedComments?: Comment[];
  onLoadDeletedComments?: () => Promise<void>;
  showDeletedCommentsTab?: boolean;
  documentOwnerId?: string | null;
  suggestions?: Suggestion[];
  onDismissSuggestion?: (suggestion: Suggestion) => Promise<void>;
  onScrollToSuggestion?: (suggestion: Suggestion) => void;
  canDismissSuggestion?: (suggestion: Suggestion) => boolean;
  getIssueTypeLabel?: (issueType: string) => string;
  t?: (key: string) => string;
  showComments?: boolean;
  showSuggestions?: boolean;
  showExport?: boolean;
  isExportActive?: boolean;
  onExportClick?: () => void;
  mentionableEntities?: MentionableEntity[];
};

export const CommentSidebar: React.FC<CommentSidebarProps> = ({
  editor = null,
  documentId,
  isReadOnly = false,
  comments = [],
  onLoadComments,
  deletedComments = [],
  onLoadDeletedComments,
  showDeletedCommentsTab = false,
  documentOwnerId = null,
  suggestions = [],
  onDismissSuggestion,
  onScrollToSuggestion,
  canDismissSuggestion,
  getIssueTypeLabel = (issueType: string) => issueType,
  t = (key: string) => key,
  showComments = true,
  showSuggestions = true,
  showExport = false,
  isExportActive = false,
  onExportClick,
  mentionableEntities = [],
}) => {
  const { open } = useCommentSidebar();
  const sidebar = useCommentSidebarShadcn();
  const [commentsRefreshTrigger, setCommentsRefreshTrigger] = useState<number>(0);
  const [commentsTab, setCommentsTab] = useState<'pending' | 'deleted'>('pending');
  const [activeView, setActiveView] = useState<CommentSidebarView>('comments');

  useEffect(() => {
    if (!showDeletedCommentsTab || commentsTab !== 'deleted' || !onLoadDeletedComments) {
      return;
    }
    void onLoadDeletedComments();
  }, [showDeletedCommentsTab, commentsTab, onLoadDeletedComments]);

  const collapseSidebar = (): void => {
    if (sidebar.isMobile) {
      sidebar.setOpenMobile(false);
    } else {
      sidebar.setOpen(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${COMMENT_SIDEBAR_COOKIE_NAME}=false; path=/; max-age=${COMMENT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    }
  };

  const openView = (view: CommentSidebarView): void => {
    if (isExportActive && onExportClick) {
      onExportClick();
    }

    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === view) {
        sidebar.setOpenMobile(false);
        return;
      }
      setActiveView(view);
      sidebar.setOpenMobile(true);
      return;
    }

    if (open && activeView === view) {
      collapseSidebar();
      return;
    }

    setActiveView(view);
    sidebar.setOpen(true);
    if (typeof document !== 'undefined') {
      document.cookie = `${COMMENT_SIDEBAR_COOKIE_NAME}=true; path=/; max-age=${COMMENT_SIDEBAR_COOKIE_MAX_AGE}`;
    }
  };

  const handleExportClick = (): void => {
    onExportClick?.();
    if (sidebar.isMobile) {
      sidebar.setOpenMobile(false);
      return;
    }
    if (open) {
      collapseSidebar();
    }
  };

  const renderComments = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    if (showDeletedCommentsTab && onLoadDeletedComments) {
      return (
        <Tabs
          value={commentsTab}
          onValueChange={(value) => setCommentsTab(value as 'pending' | 'deleted')}
          className="flex flex-col h-full min-h-0"
        >
          <TabsList className="h-7 mb-3 flex-shrink-0">
            <TabsTrigger value="pending" className="text-xs px-2 py-1 h-6">
              {t('pending')}
            </TabsTrigger>
            <TabsTrigger value="deleted" className="text-xs px-2 py-1 h-6">
              {t('comments_resolved_tab')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="flex-1 overflow-y-auto min-h-0 mt-0">
            <Comments
              editor={editor}
              documentId={documentId}
              isReadOnly={isReadOnly}
              isVisible={commentsTab === 'pending'}
              refreshTrigger={commentsRefreshTrigger}
              isFocusMode={false}
              comments={comments}
              onLoadComments={onLoadComments}
              documentOwnerId={documentOwnerId}
              hideHeading={true}
              mentionableEntities={mentionableEntities}
            />
          </TabsContent>
          <TabsContent value="deleted" className="flex-1 overflow-y-auto min-h-0 mt-0">
            <Comments
              editor={editor}
              documentId={documentId}
              isReadOnly={isReadOnly}
              isVisible={commentsTab === 'deleted'}
              refreshTrigger={commentsRefreshTrigger}
              isFocusMode={false}
              comments={deletedComments}
              onLoadComments={onLoadDeletedComments}
              documentOwnerId={documentOwnerId}
              hideHeading={true}
              commentThreadList="resolved"
              mentionableEntities={mentionableEntities}
            />
          </TabsContent>
        </Tabs>
      );
    }
    return (
      <Comments
        editor={editor}
        documentId={documentId}
        isReadOnly={isReadOnly}
        isVisible={true}
        refreshTrigger={commentsRefreshTrigger}
        isFocusMode={false}
        comments={comments}
        onLoadComments={onLoadComments}
        documentOwnerId={documentOwnerId}
        mentionableEntities={mentionableEntities}
      />
    );
  };

  const renderSuggestions = (): React.ReactNode => {
    const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor ?? null, suggestions);

    return (
      <div className="flex flex-col flex-1 min-h-0 h-full min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-4 flex-shrink-0">
          <h2 className="text-lg font-semibold">{t('suggestions')}</h2>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {pendingSuggestions.length === 0 ? (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              {t('no_suggestions')}
            </div>
          ) : (
            <div className="flex flex-col">
              {pendingSuggestions.map((suggestion, index) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onScrollToSuggestion={onScrollToSuggestion}
                  onDismissSuggestion={
                    canDismissSuggestion?.(suggestion) ? onDismissSuggestion : undefined
                  }
                  t={t}
                  getIssueTypeLabel={getIssueTypeLabel}
                  showDivider={index < pendingSuggestions.length - 1}
                  showOriginalAtTop={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActivePanel = (): React.ReactNode => {
    if (activeView === 'suggestions' && showSuggestions) {
      return renderSuggestions();
    }
    if (showComments) {
      return renderComments();
    }
    if (showSuggestions) {
      return renderSuggestions();
    }
    return null;
  };

  const hasDrawerViews = showComments || showSuggestions;

  const renderViewButtons = (options?: { includeLabels?: boolean; className?: string }): React.ReactNode => {
    const includeLabels = options?.includeLabels === true;
    const buttonClassName = options?.className;
    const isPanelOpen = !isExportActive && (open || sidebar.openMobile);

    return (
      <>
        {hasDrawerViews && (
          <SidebarMenuItem className="flex justify-center">
            <CommentSidebarMenuButton
              onClick={collapseSidebar}
              tooltip={t('close_drawer')}
              disabled={!isPanelOpen}
              className={buttonClassName}
            >
              <ChevronRight className="h-5 w-5" />
              {includeLabels ? <span>{t('close_drawer')}</span> : null}
            </CommentSidebarMenuButton>
          </SidebarMenuItem>
        )}
        {showSuggestions && (
          <SidebarMenuItem className="flex justify-center">
            <CommentSidebarMenuButton
              onClick={() => openView('suggestions')}
              tooltip={t('suggestions')}
              isActive={isPanelOpen && activeView === 'suggestions'}
              className={buttonClassName}
            >
              <Lightbulb className="h-5 w-5" />
              {includeLabels ? <span>{t('suggestions')}</span> : null}
            </CommentSidebarMenuButton>
          </SidebarMenuItem>
        )}
        {showComments && (
          <SidebarMenuItem className="flex justify-center">
            <CommentSidebarMenuButton
              onClick={() => openView('comments')}
              tooltip={t('comments')}
              isActive={isPanelOpen && activeView === 'comments'}
              className={buttonClassName}
            >
              <MessageSquare className="h-5 w-5" />
              {includeLabels ? <span>{t('comments')}</span> : null}
            </CommentSidebarMenuButton>
          </SidebarMenuItem>
        )}
        {showExport && (
          <SidebarMenuItem className="flex justify-center">
            <CommentSidebarMenuButton
              onClick={handleExportClick}
              tooltip={t('export')}
              isActive={isExportActive}
              className={buttonClassName}
            >
              <Download className="h-5 w-5" />
              {includeLabels ? <span>{t('export')}</span> : null}
            </CommentSidebarMenuButton>
          </SidebarMenuItem>
        )}
      </>
    );
  };

  const getSidebarWidthMobile = (): string => {
    return COMMENT_SIDEBAR_WIDTH_MOBILE;
  };

  if (sidebar.isMobile) {
    if (showExport && !hasDrawerViews) {
      return (
        <div className="fixed right-3 bottom-24 z-20 md:hidden">
          <Button
            variant={isExportActive ? 'secondary' : 'outline'}
            size="icon"
            className="h-11 w-11 rounded-full shadow-md bg-sidebar border-sidebar-border"
            onClick={handleExportClick}
            aria-label={t('export')}
            title={t('export')}
          >
            <Download className="h-5 w-5" />
          </Button>
        </div>
      );
    }

    return (
      <>
        <Sheet open={sidebar.openMobile} onOpenChange={sidebar.setOpenMobile}>
          <SheetContent
            side="right"
            className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
            style={
              {
                '--sidebar-width': getSidebarWidthMobile(),
              } as React.CSSProperties
            }
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Comments Sidebar</SheetTitle>
              <SheetDescription>Comments sidebar panel</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-4 pb-2">
                  <SidebarGroup className={cn(sidebar.state === 'collapsed' && 'p-1')}>
                    <SidebarGroupContent>
                      <SidebarMenu className={cn(sidebar.state === 'collapsed' && 'items-center')}>
                        {renderViewButtons({ includeLabels: true })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                </div>
                <div className="p-6 pt-2">
                  {renderActivePanel()}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  const drawerOpen = open && hasDrawerViews;

  return (
    <div
      className="group peer hidden text-sidebar-foreground md:block"
      data-state={sidebar.state}
      data-side="right"
    >
      <div
        className={cn(
          'relative bg-transparent transition-[width] duration-200 ease-linear',
        )}
        style={
          {
            width: drawerOpen ? `calc(${COMMENT_SIDEBAR_WIDTH_ICON} + ${COMMENT_SIDEBAR_WIDTH})` : COMMENT_SIDEBAR_WIDTH_ICON,
          } as React.CSSProperties
        }
      />
      <div
        className={cn(
          'fixed hidden md:flex flex-row-reverse',
          'right-0',
          'inset-y-0 h-svh'
        )}
        style={{ zIndex: 1 }}
      >
        <div
          className="flex h-full flex-col bg-sidebar shrink-0"
          style={{
            width: COMMENT_SIDEBAR_WIDTH_ICON,
            borderLeft: drawerOpen ? '1px solid #f3f4f6' : 'none',
          }}
        >
          <SidebarHeader className="flex flex-col h-full">
            <SidebarGroup className="p-1">
              <SidebarGroupContent>
                <SidebarMenu className="items-center">
                  {renderViewButtons({ className: '!size-8 !p-2 !justify-center !text-center' })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarHeader>
        </div>
        <div
          className={cn(
            "flex h-full flex-col bg-sidebar overflow-hidden relative transition-all duration-300 ease-in-out",
            drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          style={{
            width: drawerOpen ? COMMENT_SIDEBAR_WIDTH : '0px',
            borderRight: drawerOpen ? '1px solid #f3f4f6' : 'none',
            transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
          }}
        >
          {drawerOpen && (
            <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
              <SidebarContent className="p-6 flex flex-col h-full min-h-0">
                <div className="flex-1 overflow-y-auto min-h-0">
                  {renderActivePanel()}
                </div>
              </SidebarContent>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CommentSidebarTrigger: React.FC<React.ComponentProps<typeof Button>> = ({ className, ...props }) => {
  const { t } = useLocale();
  const { toggleSidebar } = useCommentSidebar();

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-10 w-10', className)}
      onClick={toggleSidebar}
      aria-label={t('toggle_comments_sidebar')}
      {...props}
    >
      <PanelRight className="h-10 w-10" />
    </Button>
  );
};

